/**
 * Pie chart. Slices are the categories of `encoding.groupKey`. Slice values come
 * from `encoding.yKey` (summed per category); with no `yKey`, each slice counts
 * the occurrences of its category. Slice colors use `groupColors`, falling back
 * to the palette. No cartesian axes.
 */

import type { ChartTypeDefinition, ChartTrace } from '../types';
import { registerChart } from '../registry';
import { colorAt } from '../theme/palette';
import { toFiniteNumber } from '../shared/number';
import { uniqueValues } from '../shared/uniqueValues';

export const pieChart: ChartTypeDefinition = {
  id: 'pie',
  label: 'Pie',
  // A pie reads best as a circle, so the card stays roughly square.
  defaultAspectRatio: 1.0,
  capabilities: {
    multiSeries: false,
    supportsXKey: false,
    supportsYKey: true,
    supportsGroupBy: true,
    supportsLimits: false,
    filterable: true,
    supportsStacked: false,
    legendByDefault: false,
  },
  buildTraces(model, ctx) {
    const { data, encoding, groupColors } = model;
    const groupKey = encoding.groupKey ?? 'category';
    const yKey = encoding.yKey;

    // Labels (slices) always come from the data's groupKey; groupColors only
    // overrides per-slice color, never the set of slices shown.
    const labels = uniqueValues(data, groupKey);

    const values = labels.map((label) => {
      const rows = data.filter((d) => String(d[groupKey]) === label);
      if (!yKey) return rows.length;
      // Sum only real numbers: a missing/blank/non-numeric cell is skipped
      // rather than coerced to 0, which would otherwise poison the slice.
      return rows.reduce((sum, d) => {
        const n = toFiniteNumber(d[yKey]);
        return n === null ? sum : sum + n;
      }, 0);
    });

    const colors = labels.map((label, i) => groupColors[label] || colorAt(i, ctx.palette));

    const trace: ChartTrace = {
      labels,
      values,
      type: 'pie',
      marker: { colors },
    };
    return [trace];
  },
};

registerChart(pieChart);
